import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Popover} from '@Components/popover';
import {PopoverDefaultAlignment} from '@Components/popover/popover.types';
import {ELEMENTS_BLOCK_ANCHOR_ID} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import StockPanel from '@Panels/stock-panel/stock-panel';
import AiImagePanel from '@Panels/ai-image-panel';
import AiVideoPanel from '@Panels/ai-video-panel';
import {MediaTypePrompt} from '@Panels/ai-image-panel/ai-image-panel.types';
import type {ElementData} from '@Libraries/add-media-library';
import {AiMenuOpenSource} from '@Libraries/ga-events';
import styles from './email-stock-media-popup.module.scss';
import type {EmailStockPopupRequest, EmailStockPopupView} from './email-stock-media-popup.types';
import {
  EMAIL_STOCK_POPUP_AI_IMAGE_PANEL_ID,
  EMAIL_STOCK_POPUP_AI_VIDEO_PANEL_ID,
  EMAIL_STOCK_POPUP_ARROW_HEIGHT_PX,
  EMAIL_STOCK_POPUP_ARROW_WIDTH_PX,
  EMAIL_STOCK_POPUP_DEFAULT_PANEL_ID,
  EMAIL_STOCK_POPUP_OFFSET_PX,
} from './email-stock-media-popup.types';
import {closeEmailStockMediaPopup, getEmailStockPopupAnchor, registerEmailStockMediaPopupHandlers} from './email-stock-popup-controller';
import {EmailMediaOptionsPanel} from './components/email-media-options-panel/email-media-options-panel';
import type {MediaOptionTile} from './components/email-media-options-panel/email-media-options-panel.types';

/**
 * Always-mounted host (a sibling of the email maker layout) for the editor's stock-media popup. It floats a
 * `StockPanel` — or the AI generators / the Video source chooser — anchored to the left panel, mirroring the
 * poster editor's popup. Outside-click / Esc and a single pick both close it (every email flow is a one-shot
 * single-select replace).
 */
export function EmailStockMediaPopup(): ReactElement | null {
  const [request, setRequest] = useState<EmailStockPopupRequest | null>(null);
  const [activeView, setActiveView] = useState<EmailStockPopupView>('stock');
  const anchorRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    return registerEmailStockMediaPopupHandlers(
      (requestedPopup): void => {
        anchorRef.current = getEmailStockPopupAnchor();
        setActiveView(requestedPopup.initialView ?? 'stock');
        setRequest(requestedPopup);
      },
      (): void => {
        setRequest(null);
      }
    );
  }, []);

  if (!request || !anchorRef.current) {
    return null;
  }

  const close = (): void => {
    closeEmailStockMediaPopup();
  };

  // Back returns to the screen that opened the popup (e.g. reopens the block panel); plain close (X / outside click) does not.
  const handleNavigateBack = (): void => {
    const navigateBack = request.onNavigateBack;
    close();
    navigateBack?.();
  };

  const handleAddSelected = (items: ElementData[]): void => {
    void request.overrides.onAddSelected?.(items);
    close();
  };

  // The stock grid fires onStartAddSelected itself; the AI generators only report the pick, so the loading
  // overlay on the target component is started here (parity with the TEXT_TO_IMAGE modal flow).
  const handleAiAddSelected = (items: ElementData[]): void => {
    request.overrides.onStartAddSelected?.(items.length);
    handleAddSelected(items);
  };

  // Tiles that open a modal (uploads / embed) leave the popup first.
  const closeThenRun = (callback?: () => void): (() => void) => {
    return (): void => {
      close();
      callback?.();
    };
  };

  const showVideoOptions = (): void => {
    setActiveView('video-options');
  };

  const showImageOptions = (): void => {
    setActiveView('image-options');
  };

  /** The chooser view to return to when a sub-view's Back is hit, or null when Back should exit the popup instead. */
  const getBackToOptionsView = (): (() => void) | null => {
    if (request.exitOnSubViewBack) {
      return null;
    }
    if (request.initialView === 'video-options') {
      return showVideoOptions;
    }
    if (request.initialView === 'image-options') {
      return showImageOptions;
    }
    return null;
  };

  const getVideoOptionTiles = (): MediaOptionTile[] => {
    const notifySourceSelected = (): void => {
      request.videoOptions?.onSourceSelected?.();
    };
    return [
      {
        icon: 'icon-upload',
        label: window.i18next.t('pmwjs_myuploads'),
        onClick: (): void => {
          notifySourceSelected();
          closeThenRun(request.videoOptions?.onMyUploads)();
        },
      },
      {
        icon: 'icon-media',
        label: window.i18next.t('pmwjs_explore_media'),
        onClick: (): void => {
          notifySourceSelected();
          setActiveView('stock');
        },
      },
      {
        icon: 'icon-video-ai',
        label: window.i18next.t('pmwjs_ai_videos'),
        isAi: true,
        onClick: (): void => {
          notifySourceSelected();
          setActiveView('ai-videos');
        },
      },
      {
        icon: 'icon-code',
        label: window.i18next.t('pmwjs_embed_videos'),
        onClick: (): void => {
          notifySourceSelected();
          closeThenRun(request.videoOptions?.onEmbedVideo)();
        },
      },
    ];
  };

  const getImageOptionTiles = (): MediaOptionTile[] => {
    return [
      {
        icon: 'icon-upload',
        label: window.i18next.t('pmwjs_myuploads'),
        onClick: closeThenRun(request.imageOptions?.onMyUploads),
      },
      {
        icon: 'icon-media',
        label: window.i18next.t('pmwjs_explore_media'),
        onClick: (): void => {
          setActiveView('stock');
        },
      },
      {
        icon: 'icon-ai-image',
        label: window.i18next.t('pmwjs_ai_images'),
        isAi: true,
        onClick: (): void => {
          setActiveView('ai-images');
        },
      },
      {
        icon: 'icon-designs',
        label: window.i18next.t('pmwjs_my_designs'),
        onClick: closeThenRun(request.imageOptions?.onMyDesigns),
      },
    ];
  };

  const renderPanel = (): ReactElement => {
    if (activeView === 'video-options') {
      return <EmailMediaOptionsPanel title={window.i18next.t('pmwjs_video')} tiles={getVideoOptionTiles()} onBack={handleNavigateBack} />;
    }
    if (activeView === 'image-options') {
      return <EmailMediaOptionsPanel title={window.i18next.t('pmwjs_image')} tiles={getImageOptionTiles()} onBack={handleNavigateBack} />;
    }
    if (activeView === 'ai-images' || activeView === 'ai-stickers') {
      return (
        <AiImagePanel
          key={activeView}
          panelId={EMAIL_STOCK_POPUP_AI_IMAGE_PANEL_ID}
          ga4Source={AiMenuOpenSource.MEDIA_DRAWER}
          mediaType={activeView === 'ai-stickers' ? MediaTypePrompt.STICKER : MediaTypePrompt.PHOTO}
          onAddSelected={handleAiAddSelected}
          onBack={getBackToOptionsView() ?? handleNavigateBack}
          onClose={close}
          isOpenedInPopOver
        />
      );
    }
    if (activeView === 'ai-videos') {
      return (
        <AiVideoPanel
          panelId={EMAIL_STOCK_POPUP_AI_VIDEO_PANEL_ID}
          onAddSelected={handleAiAddSelected}
          onBack={getBackToOptionsView() ?? handleNavigateBack}
          onClose={close}
          isFullScreen={false}
          isOpenedInPopOver
        />
      );
    }
    const {onClose: _ignoredOnClose, onAddSelected: _ignoredOnAddSelected, ...overrideRest} = request.overrides;
    return (
      <StockPanel
        key={overrideRest.panelId ?? EMAIL_STOCK_POPUP_DEFAULT_PANEL_ID}
        {...overrideRest}
        onAddSelected={handleAddSelected}
        onClose={close}
        onBack={getBackToOptionsView() ?? handleNavigateBack}
      />
    );
  };

  // Pin the arrow to the left-menu tile the flow originated from (e.g. the Image tile for Image-panel flows);
  // Elements is the default — every other media flow conceptually originates there (mirrors the block panels' arrow).
  const getArrowOffsetTowardsOriginTile = (): number | null => {
    const originTile = document.getElementById(request.arrowAnchorTileId ?? ELEMENTS_BLOCK_ANCHOR_ID);
    const anchor = anchorRef.current;
    if (!originTile || !anchor) {
      return null;
    }
    const tileRect = originTile.getBoundingClientRect();
    return tileRect.top + tileRect.height / 2 - anchor.getBoundingClientRect().top - EMAIL_STOCK_POPUP_ARROW_WIDTH_PX / 2;
  };

  return (
    <Popover
      referenceElementRef={anchorRef}
      popoverCloseFunction={close}
      alignment={PopoverDefaultAlignment.RIGHT_START}
      // Popover adds the arrow height to the offset when hasArrow is set; subtract it so the visual gap matches the block panels.
      popoverOffset={EMAIL_STOCK_POPUP_OFFSET_PX - EMAIL_STOCK_POPUP_ARROW_HEIGHT_PX}
      scrollAnchorIntoView={false}
      hasAutoPlacement={false}
      hasArrow
      arrowWidth={EMAIL_STOCK_POPUP_ARROW_WIDTH_PX}
      arrowHeight={EMAIL_STOCK_POPUP_ARROW_HEIGHT_PX}
      staticOffsetForArrow={getArrowOffsetTowardsOriginTile()}
      className={styles.popover}
    >
      <div className={`${styles.panelContainer} ${activeView === 'video-options' || activeView === 'image-options' ? styles.fitContent : ''}`}>{renderPanel()}</div>
    </Popover>
  );
}
